import { useEffect, useState } from "react";

/**
 * Read Malay text aloud with the device's own voice (no server, no cost).
 * Only a Malay voice, or an Indonesian one (same syllable sounds: "ca" = cha, "c" never k), is used.
 * If the device has neither we stay silent instead of letting an English voice mispronounce
 * ("ca" read as "ka") and tell the parent how to add one.
 */
export type VoiceStatus = { state: "loading" | "ok" | "none" | "unsupported"; name?: string; lang?: string };

export const canSpeak = () => typeof window !== "undefined" && "speechSynthesis" in window && typeof SpeechSynthesisUtterance !== "undefined";

function malayVoice(): SpeechSynthesisVoice | null {
  const vs = window.speechSynthesis.getVoices();
  const norm = (l: string) => l.toLowerCase().replace("_", "-");
  return vs.find((v) => norm(v.lang).startsWith("ms"))
    ?? vs.find((v) => /^(id|in)(-|$)/.test(norm(v.lang)))
    ?? null;
}

let cached: SpeechSynthesisVoice | null = null;
let ready: Promise<SpeechSynthesisVoice | null> | null = null;

/** Voices load asynchronously (empty list at first on Chrome/Safari); wait up to ~2 s for them. */
function loadVoice(): Promise<SpeechSynthesisVoice | null> {
  if (!canSpeak()) return Promise.resolve(null);
  if (cached) return Promise.resolve(cached);
  ready ??= new Promise((resolve) => {
    const done = () => { cached = malayVoice(); if (cached || window.speechSynthesis.getVoices().length) { resolve(cached); return true; } return false; };
    if (done()) return;
    const onChange = () => { if (done()) window.speechSynthesis.removeEventListener("voiceschanged", onChange); };
    window.speechSynthesis.addEventListener("voiceschanged", onChange);
    let tries = 0;
    const poll = setInterval(() => { if (done() || ++tries > 20) { clearInterval(poll); resolve(cached); } }, 100);
  });
  return ready.then((v) => { if (!v) ready = null; return v; }); // retry later if none yet (voices can arrive late)
}

export function useVoice(): VoiceStatus {
  const [s, setS] = useState<VoiceStatus>(() => (canSpeak() ? { state: "loading" } : { state: "unsupported" }));
  useEffect(() => {
    if (!canSpeak()) return;
    let live = true;
    void loadVoice().then((v) => { if (live) setS(v ? { state: "ok", name: v.name, lang: v.lang } : { state: "none" }); });
    return () => { live = false; };
  }, []);
  return s;
}

function say(v: SpeechSynthesisVoice, text: string, rate: number) {
  try {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.voice = v;
    u.lang = v.lang;
    u.rate = rate;
    window.speechSynthesis.speak(u);
  } catch { /* speech is a bonus; never break the page */ }
}

export function speak(text: string, rate = 0.8): void {
  if (!canSpeak()) return;
  if (cached) { say(cached, text, rate); return; } // synchronous inside the tap (iOS needs this)
  void loadVoice().then((v) => { if (v) say(v, text, rate); }); // never fall back to an English voice
}

/** "ba… ju… baju": each syllable slowly, then the whole word. */
export function speakWord(syllables: string[], word: string): void {
  speak(syllables.length > 1 ? `${syllables.join(", ")}. ${word}` : word, 0.7);
}

/** English fallback voice (device voice), used only when an English clip is missing: British or Malaysian English first. */
export function speakEn(text: string, rate = 0.85): void {
  if (!canSpeak()) return;
  const vs = window.speechSynthesis.getVoices();
  const norm = (l: string) => l.toLowerCase().replace("_", "-");
  const v = vs.find((x) => norm(x.lang) === "en-gb") ?? vs.find((x) => norm(x.lang) === "en-my") ?? vs.find((x) => norm(x.lang).startsWith("en")) ?? null;
  try {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    if (v) { u.voice = v; u.lang = v.lang; } else u.lang = "en-GB";
    u.rate = rate;
    window.speechSynthesis.speak(u);
  } catch { /* speech is a bonus; never break the page */ }
}
